/**
 * GEDCOM Encoding Detection Utility
 *
 * Detects character encoding of GEDCOM files through:
 * 1. BOM (Byte Order Mark) detection
 * 2. GEDCOM CHAR tag parsing from header
 * 3. Fallback to UTF-8
 *
 * Supported encodings:
 * - UTF-8 (with or without BOM)
 * - UTF-16 LE/BE
 * - CP1252 (Windows-1252)
 */

export interface EncodingDetectionResult {
  encoding: 'UTF-8' | 'UTF-16' | 'CP1252' | 'UNKNOWN';
  confidence: 'high' | 'medium' | 'low';
  source: 'BOM' | 'CHAR_TAG' | 'FALLBACK';
  warning?: string;
}

/**
 * Detect encoding from GEDCOM file buffer
 */
export const detectEncoding = (buffer: ArrayBuffer): EncodingDetectionResult => {
  const bytes = new Uint8Array(buffer);

  // Step 1: Check for BOM (Byte Order Mark)
  const bomResult = detectBOM(bytes);
  if (bomResult) {
    return bomResult;
  }

  // Step 2: Bootstrap parse as UTF-8 to read CHAR tag
  const charTagResult = detectFromCharTag(bytes);
  if (charTagResult) {
    return charTagResult;
  }

  // Step 3: Fallback to UTF-8
  return {
    encoding: 'UTF-8',
    confidence: 'low',
    source: 'FALLBACK',
    warning: 'Could not detect encoding from BOM or CHAR tag. Defaulting to UTF-8.',
  };
};

/**
 * Detect encoding from BOM (Byte Order Mark)
 */
const detectBOM = (bytes: Uint8Array): EncodingDetectionResult | null => {
  // UTF-8 BOM: 0xEF 0xBB 0xBF
  if (bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    return {
      encoding: 'UTF-8',
      confidence: 'high',
      source: 'BOM',
    };
  }

  // UTF-16 BE BOM: 0xFE 0xFF
  if (bytes.length >= 2 && bytes[0] === 0xfe && bytes[1] === 0xff) {
    return {
      encoding: 'UTF-16',
      confidence: 'high',
      source: 'BOM',
    };
  }

  // UTF-16 LE BOM: 0xFF 0xFE
  if (bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xfe) {
    return {
      encoding: 'UTF-16',
      confidence: 'high',
      source: 'BOM',
    };
  }

  return null;
};

/**
 * Detect encoding from GEDCOM CHAR tag in header
 */
const detectFromCharTag = (bytes: Uint8Array): EncodingDetectionResult | null => {
  try {
    // Bootstrap parse first 2000 bytes as UTF-8 to find CHAR tag
    const headerText = new TextDecoder('utf-8').decode(bytes.slice(0, Math.min(2000, bytes.length)));

    // Extract CHAR tag: "1 CHAR <encoding>"
    const charMatch = /1\s+CHAR\s+(.+)/i.exec(headerText);
    if (!charMatch) {
      return null;
    }

    const charValue = charMatch[1].trim().toUpperCase();

    // Extract SOUR tag for vendor-specific heuristics: "1 SOUR <source>"
    const sourMatch = /1\s+SOUR\s+(.+)/i.exec(headerText);
    const source = sourMatch?.[1]?.trim().toUpperCase();

    // Apply vendor-specific heuristics
    const vendorOverride = applyVendorHeuristics(charValue, source);
    if (vendorOverride) {
      return vendorOverride;
    }

    // Standard CHAR tag mappings
    if (charValue === 'UTF-8' || charValue === 'UTF8') {
      return {
        encoding: 'UTF-8',
        confidence: 'high',
        source: 'CHAR_TAG',
      };
    }

    if (charValue === 'UNICODE') {
      return {
        encoding: 'UTF-16',
        confidence: 'high',
        source: 'CHAR_TAG',
      };
    }

    if (charValue === 'ANSI' || charValue === 'ASCII' || charValue === 'WINDOWS-1252') {
      return {
        encoding: 'CP1252',
        confidence: 'medium',
        source: 'CHAR_TAG',
        warning: 'ANSI/ASCII detected. Treating as Windows-1252 (CP1252).',
      };
    }

    if (charValue === 'ANSEL') {
      return {
        encoding: 'UNKNOWN',
        confidence: 'low',
        source: 'CHAR_TAG',
        warning: 'ANSEL encoding is not fully supported. Attempting UTF-8 decode as fallback.',
      };
    }

    // Unknown CHAR value
    return {
      encoding: 'UNKNOWN',
      confidence: 'low',
      source: 'CHAR_TAG',
      warning: `Unknown CHAR tag value: "${charValue}". Defaulting to UTF-8.`,
    };
  } catch (error) {
    // Failed to decode as UTF-8
    return null;
  }
};

/**
 * Apply vendor-specific encoding heuristics
 */
const applyVendorHeuristics = (
  charValue: string,
  source: string | undefined
): EncodingDetectionResult | null => {
  // Heredis exports with "CHAR ANSI" actually use CP1252
  if (source === 'HEREDIS' && charValue === 'ANSI') {
    return {
      encoding: 'CP1252',
      confidence: 'high',
      source: 'CHAR_TAG',
      warning: 'Heredis ANSI detected. Using CP1252 encoding.',
    };
  }

  // GeneWeb exports with "CHAR ASCII" actually use CP1252
  if (source === 'GENEWEB' && charValue === 'ASCII') {
    return {
      encoding: 'CP1252',
      confidence: 'high',
      source: 'CHAR_TAG',
      warning: 'GeneWeb ASCII detected. Using CP1252 encoding.',
    };
  }

  return null;
};

/**
 * Get human-readable encoding name for display
 */
export const getEncodingDisplayName = (encoding: EncodingDetectionResult['encoding']): string => {
  switch (encoding) {
    case 'UTF-8':
      return 'UTF-8';
    case 'UTF-16':
      return 'UTF-16';
    case 'CP1252':
      return 'Windows-1252 (CP1252)';
    case 'UNKNOWN':
      return 'Unknown (UTF-8 fallback)';
  }
};

/**
 * Get confidence level display name
 */
export const getConfidenceDisplayName = (confidence: EncodingDetectionResult['confidence']): string => {
  switch (confidence) {
    case 'high':
      return 'High';
    case 'medium':
      return 'Medium';
    case 'low':
      return 'Low';
  }
};
